import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { LandingPageComponent } from "./features/landing-page/landing-page.component";
import { CanDeactivateGuard } from "./common/guards/unsaved-changes-guard";
import { AccessDeniedComponent } from "./features/access-denied/access-denied.component";
import { PayorFacilitiesComponent } from "./features/corporation-management/payor-management/payor-facilities/payor-facilities.component";
import { PayorFormComponent } from "./features/corporation-management/payor-management/payor-form-management.component";
import { PayorProfileComponent } from "./features/corporation-management/payor-management/payor-profile/payor-profile.component";
import { ValidateComponent } from "./features/validate/validate.component";
import { userValidatedGuard } from "./common/guards/user-validated.guard";
import { CoSignComponent } from "./features/co-sign/co-sign.component";
import { AutoForecastComponent } from "./features/patients/forecast/auto-forecast/auto-forecast.component";
import { BiDashboardComponent } from "./features/bi-dashboard/bi-dashboard.component";
import { appRoute } from "./app-route.constants";
import { FeatureAccessGuard, getFeatureAccessGuardData } from "./common/guards/feature-access.guard";
import { Features } from "./common/enums/features.enum";
import { mobileBlockGuard } from "./common/guards/mobile-block.guard";

const routes: Routes = [
  {
    path: "validate",
    component: ValidateComponent,
  },
  {
    path: "docreview",
    loadChildren: () => import("./features/esignature/esignature.routes").then((mod) => mod.esignatureRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "practitioner",
    loadChildren: () => import("./features/practitioner-setup/routes").then((mod) => mod.practitionerRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "facility",
    loadChildren: () => import("./features/facility/routes").then((mod) => mod.facilityRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "patient",
    loadChildren: () => import("./features/patients/routes").then((mod) => mod.patientRoutes),
    canActivate: [userValidatedGuard],
    canActivateChild: [userValidatedGuard],
  },
  {
    path: "user",
    loadChildren: () => import("./features/user-management/routes").then((mod) => mod.userRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "corporation",
    loadChildren: () => import("./features/corporation-management/routes").then((mod) => mod.corporationRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "security-group",
    loadChildren: () => import("./features/security-group/routes").then((mod) => mod.securityGroupRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "time-card",
    loadChildren: () => import("./features/time-card/routes").then((mod) => mod.timeCardRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "arc-codes",
    loadChildren: () => import("./features/arc-codes/routes").then((mod) => mod.arcCodeRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "payor/:payorId",
    component: PayorProfileComponent,
    canDeactivate: [CanDeactivateGuard],
    canActivate: [userValidatedGuard, FeatureAccessGuard],
    data: getFeatureAccessGuardData(Features.CORP_SETTINGS_PAYOR_PLAN),
    children: [
      {
        path: "settings",
        component: PayorFormComponent,
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: "facilities",
        canDeactivate: [CanDeactivateGuard],
        component: PayorFacilitiesComponent,
      },
      { path: "", redirectTo: "settings", pathMatch: "full" },
    ],
  },
  {
    path: "esignature",
    loadChildren: () => import("./features/settings/routes").then((mod) => mod.settingsRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "set-up",
    loadChildren: () => import("./features/set-up/routes").then((mod) => mod.setUpRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "agenda",
    loadChildren: () => import("./features/agenda/routes").then((mod) => mod.agendaRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: "reports",
    loadChildren: () => import("./features/reports/routes").then((mod) => mod.reportRoutes),
    canActivate: [userValidatedGuard],
  },
  {
    path: appRoute.biDashboard,
    component : BiDashboardComponent,
    canActivate: [userValidatedGuard],
  },
  {
    path: "access-denied",
    component: AccessDeniedComponent,
  },
  {
    path: "home",
    component: LandingPageComponent,
    canActivate: [mobileBlockGuard, userValidatedGuard],
  },
  {
    path: "co-sign",
    component: CoSignComponent,
    canActivate: [userValidatedGuard],
  },
  {
    path: "forecast-template/:templateId",
    component: AutoForecastComponent,
    canActivate: [userValidatedGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: "close-out",
    loadChildren: () => import("./features/close-out/routes").then((mod) => mod.closeOutRoutes),
    canActivate: [userValidatedGuard],
  },
  { path: "**", redirectTo: "home" },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: "always" })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
